import { Button, Heading, Icon, Tag, Text } from "@ttc3k/trekker";
import { EventLocation } from "gql/generated";
import { WarningTriangle } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { HtmlContent } from "shared/components/molecules/HtmlContent";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import { MultilingualLangKey } from "shared/types";
import { getMultilingualString } from "shared/utils";
import { css } from "styled-system/css";
import { Box, Flex } from "styled-system/jsx";

const eventsWidgetDescriptionClassName = css({
  color: "text.mid",
  whiteSpace: "pre-wrap",
  "& a": {
    textDecoration: "underline",
  },
});

type EventInfoProps = {
  showNonOperatorWarning?: boolean;
  isCancelled?: boolean;
  lang: MultilingualLangKey;
  title: string;
  eventDetailsLink?: string;
  handleEventDetailsLinkClick: () => void;
  eventOrganizer: EventLocation;
  eventUrl: string;
  eventPhone: string;
  eventEmail: string;
  handleWindowOpen: (url: string, trackWithPostHog: boolean) => void;
  description: string;
};

export const EventInfo = ({
  showNonOperatorWarning = false,
  isCancelled = false,
  lang,
  title,
  eventDetailsLink,
  handleEventDetailsLinkClick,
  eventOrganizer,
  eventUrl,
  eventPhone,
  eventEmail,
  handleWindowOpen,
  description,
}: EventInfoProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });
  const { t: tApp } = useTranslation("events", { keyPrefix: "APP.CARD.STATUS" });

  return (
    <Flex part="details__info-container" flexDir="column" gap="300">
      {isCancelled && (
        <Tag
          part="details__tag_cancelled"
          visual="error"
          mode="light"
          bg="bg.error.light"
          color="text.error.dark"
        >
          {tApp("CANCELLED")}
        </Tag>
      )}
      <Flex
        part="details__info-event-name-container"
        flexDir="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="200"
      >
        <Heading part="details__title" size={"h3"}>
          {title}
        </Heading>
        {eventDetailsLink && (
          <Button
            part={"details__btn-see-more"}
            visual="primary"
            flexShrink={0}
            py="9px"
            type="button"
            onClick={handleEventDetailsLinkClick}
          >
            {t("BUTTONS.SEE_MORE")}
          </Button>
        )}
      </Flex>

      {showNonOperatorWarning &&
        (!eventOrganizer.locationInfo ||
          !eventOrganizer.locationInfo._entityId) && (
          <Flex
            flexDir="row"
            gap="200"
            alignItems="center"
            border="2px solid"
            borderColor="border.highlight"
            borderRadius="100"
            bgColor="bg.highlight.lightest"
            py="150"
            px="200"
          >
            <Icon
              Element={WarningTriangle}
              w="24px"
              h="24px"
              color="icon.highlight.dark"
              flexShrink={0}
            />
            <Text visual="bodyRegular" color="text.highlight.dark">
              {t("WARNINGS.NON_OPERATOR_WARNING")}
            </Text>
          </Flex>
        )}

      {/* Event organizer */}
      {eventOrganizer && (
        <Flex part="details__info-organizer-container" flexDir="column" gap="0">
          <Text
            part="details__organizer-title"
            visual="smallMedium"
            color="text.mid"
          >
            {t("LABELS.ORGANIZED_BY")}
          </Text>
          <Heading part="details__organizer-name" size={"h5"}>
            {getMultilingualString(eventOrganizer.venue, lang)}
          </Heading>
        </Flex>
      )}

      {/* Event contact info */}
      <Flex
        part="details__info-contact-info-container"
        justifyContent={"space-between"}
        alignItems={"center"}
        gap="150"
      >
        {eventUrl && (
          <Button
            part="details__btn-contact"
            visual="primary"
            mode="light"
            type="button"
            onClick={() => handleWindowOpen(eventUrl, true)}
            w="full"
          >
            {t("BUTTONS.WEBSITE")}
          </Button>
        )}
        {eventPhone && (
          <Button
            part="details__btn-contact"
            visual="primary"
            mode="light"
            type="button"
            onClick={() => handleWindowOpen(`tel:${eventPhone}`, false)}
            w="full"
          >
            {t("BUTTONS.PHONE")}
          </Button>
        )}
        {eventEmail && (
          <Button
            part="details__btn-contact"
            visual="primary"
            mode="light"
            type="button"
            onClick={() => handleWindowOpen(`mailto:${eventEmail}`, false)}
            w="full"
          >
            {t("BUTTONS.EMAIL")}
          </Button>
        )}
      </Flex>

      {/* Event description — Lexical/HTML from CMS; underline links for visibility in the embed */}
      {!isLexicalHtmlEmpty(description) && (
        <Box part="details__description">
          <HtmlContent
            html={description}
            className={eventsWidgetDescriptionClassName}
          />
        </Box>
      )}
    </Flex>
  );
};
